import React, { useState } from 'react';
import { X, Plus, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Note {
  id: string;
  type: 'system' | 'user';
  author?: string;
  timestamp: string;
  title: string;
  content: string;
}
interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  notes: Note[];
}
export function NotesModal({
  isOpen,
  onClose,
  companyName,
  notes
}: NotesModalProps) {
  const [filter, setFilter] = useState<'all' | 'system' | 'user'>('all');
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const filteredNotes = notes.filter(note => {
    if (filter === 'all') return true;
    return note.type === filter;
  });
  const handleAddNote = () => {
    // In a real app, this would save to backend
    setNewNoteContent('');
    setShowAddNote(false);
  };
  return <AnimatePresence data-id="element-2743">
      {isOpen && <>
          {/* No backdrop - drawer is part of the interface */}

          {/* Drawer - Slide in from right edge */}
          <div className="fixed top-0 right-0 bottom-0 z-50 pointer-events-none" data-id="element-2744">
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="bg-white shadow-2xl w-[380px] pointer-events-auto border-l border-gray-200 h-full flex flex-col" data-id="element-2745">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-2746">
                <div className="flex items-center gap-2" data-id="element-2747">
                  <MessageSquare className="w-5 h-5 text-gray-600" data-id="element-2748" />
                  <div data-id="element-2749">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2750">Notes</h2>
                    <p className="text-xs text-gray-500" data-id="element-2751">{companyName}</p>
                  </div>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2752">
                  <X className="w-5 h-5" data-id="element-2753" />
                </button>
              </div>

              {/* Add Note Button */}
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-2754">
                <button onClick={() => setShowAddNote(!showAddNote)} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors" data-id="element-2755">
                  <Plus className="w-4 h-4" data-id="element-2756" />
                  ADD NOTE
                </button>

                {/* Add Note Form */}
                {showAddNote && <div className="mt-4 space-y-3" data-id="element-2757">
                    <textarea value={newNoteContent} onChange={e => setNewNoteContent(e.target.value)} placeholder="Enter your note..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" rows={3} data-id="element-2758" />
                    <div className="flex gap-2" data-id="element-2759">
                      <button onClick={handleAddNote} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors" data-id="element-2760">
                        Save
                      </button>
                      <button onClick={() => {
                  setShowAddNote(false);
                  setNewNoteContent('');
                }} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors" data-id="element-2761">
                        Cancel
                      </button>
                    </div>
                  </div>}
              </div>

              {/* Filter */}
              <div className="px-6 py-3 border-b border-gray-200" data-id="element-2762">
                <select value={filter} onChange={e => setFilter(e.target.value as 'all' | 'system' | 'user')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" data-id="element-2763">
                  <option value="all" data-id="element-2764">All Notes</option>
                  <option value="system" data-id="element-2765">System Notes</option>
                  <option value="user" data-id="element-2766">User Notes</option>
                </select>
              </div>

              {/* Notes List */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-2767">
                {filteredNotes.map(note => <div key={note.id} className={`border-l-4 pl-4 py-2 ${note.type === 'system' ? 'border-gray-300 bg-gray-50' : 'border-indigo-500 bg-indigo-50'} rounded-r-lg`} data-id="element-2768">
                    {note.type === 'user' && note.author && <div className="flex items-center gap-2 mb-2" data-id="element-2769">
                        <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-2770">
                          {note.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm font-semibold text-gray-900" data-id="element-2771">
                          {note.author}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-2772">
                          {note.timestamp}
                        </span>
                      </div>}
                    {note.type === 'system' && <div className="flex items-center gap-2 mb-2" data-id="element-2773">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-2774"></div>
                        <span className="text-xs text-gray-500" data-id="element-2775">
                          {note.timestamp}
                        </span>
                      </div>}
                    <div className="font-semibold text-gray-900 text-sm mb-1" data-id="element-2776">
                      {note.title}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-2777">
                      {note.content}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}